import React, { useEffect, useRef, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useLocation } from 'react-router-dom';
import './Sidebar.css';

/** A link, or a group of links (`children`) shown as an expandable section. */
export interface NavItem {
  to?: string;
  label: string;
  iconType: string;
  children?: NavItem[];
  badge?: number;
}

const OPEN_GROUPS_KEY = 'sidebar.openGroups';

function readOpenGroups(): string[] {
  try {
    return JSON.parse(localStorage.getItem(OPEN_GROUPS_KEY) || '[]');
  } catch {
    return [];
  }
}

interface SidebarProps {
  navItems: NavItem[];
  onLogout: () => void;
  onNewTask?: () => void;
  isCollapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
  width: number;
  onWidthChange: (width: number) => void;
  mobileOpen: boolean;
  onMobileOpenChange: (open: boolean) => void;
}

const SidebarIcon: React.FC<{ type: string }> = ({ type }) => {
  const p = {
    xmlns: 'http://www.w3.org/2000/svg',
    width: 18,
    height: 18,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: '2',
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
  switch (type) {
    case 'dashboard':
      return <svg {...p}><path d="M3 12l9-9 9 9"/><path d="M5 10v10a1 1 0 001 1h4v-6h4v6h4a1 1 0 001-1V10"/></svg>;
    case 'stock':
      return <svg {...p}><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/></svg>;
    case 'orders':
      return <svg {...p}><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg>;
    case 'analytics':
      return <svg {...p}><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/><line x1="2" y1="20" x2="22" y2="20"/></svg>;
    case 'purchase-orders':
      return <svg {...p}><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>;
    case 'inputs':
      return <svg {...p}><path d="M12 2v6"/><polyline points="9 5 12 8 15 5"/><rect x="3" y="10" width="18" height="11" rx="2"/><line x1="3" y1="15" x2="21" y2="15"/></svg>;
    case 'admin':
      return <svg {...p}><circle cx="12" cy="8" r="4"/><path d="M20 21a8 8 0 10-16 0"/></svg>;
    case 'inventory':
      return <svg {...p}><rect x="4" y="2" width="3" height="20"/><rect x="9" y="2" width="1.5" height="20"/><rect x="13" y="2" width="3" height="20"/><rect x="18" y="2" width="2" height="20"/></svg>;
    case 'warehouse-orders':
      return <svg {...p}><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16"/></svg>;
    case 'lists':
      return <svg {...p}><line x1="9" y1="6" x2="21" y2="6"/><line x1="9" y1="12" x2="21" y2="12"/><line x1="9" y1="18" x2="21" y2="18"/><polyline points="3 6 4 7 6 5"/><polyline points="3 12 4 13 6 11"/><polyline points="3 18 4 19 6 17"/></svg>;
    case 'calendar':
      return <svg {...p}><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><rect x="7" y="14" width="3" height="3" rx="0.5"/></svg>;
    case 'projects':
      return <svg {...p}><rect x="2" y="9.5" width="7" height="5" rx="1.5"/><rect x="15" y="3" width="7" height="5" rx="1.5"/><rect x="15" y="16" width="7" height="5" rx="1.5"/><path d="M9 12h2.5a1.5 1.5 0 001.5-1.5V7a1.5 1.5 0 011.5-1.5H15"/><path d="M11.5 12a1.5 1.5 0 011.5 1.5V17a1.5 1.5 0 001.5 1.5H15"/></svg>;
    case 'scan':
      return <svg {...p}><path d="M3 7V5a2 2 0 012-2h2"/><path d="M17 3h2a2 2 0 012 2v2"/><path d="M21 17v2a2 2 0 01-2 2h-2"/><path d="M7 21H5a2 2 0 01-2-2v-2"/><line x1="7" y1="8" x2="7" y2="16"/><line x1="10" y1="8" x2="10" y2="16"/><line x1="13.5" y1="8" x2="13.5" y2="16"/><line x1="17" y1="8" x2="17" y2="16"/></svg>;
    case 'warehouses':
      return <svg {...p}><path d="M3 21V9l9-6 9 6v12"/><path d="M7 21v-8h10v8"/><line x1="7" y1="17" x2="17" y2="17"/></svg>;
    case 'plus':
      return <svg {...p}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>;
    case 'chevron-down':
      return <svg {...p}><polyline points="6 9 12 15 18 9"/></svg>;
    case 'hub':
      return <svg {...p}><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>;
    case 'logout':
      return <svg {...p}><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>;
    case 'hamburger':
      return <svg {...p}><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/></svg>;
    case 'close':
      return <svg {...p}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
    case 'chevron-left':
      return <svg {...p}><polyline points="15 18 9 12 15 6"/></svg>;
    case 'chevron-right':
      return <svg {...p}><polyline points="9 18 15 12 9 6"/></svg>;
    default:
      return <svg {...p}><circle cx="12" cy="12" r="4"/></svg>;
  }
};

const isActivePath = (pathname: string, to?: string) =>
  !!to && (to === '/' ? pathname === '/' : pathname === to || pathname.startsWith(`${to}/`));

const groupIsActive = (pathname: string, item: NavItem) =>
  (item.children || []).some((child) => isActivePath(pathname, child.to));

const NavLinkItem: React.FC<{
  item: NavItem;
  isCollapsed: boolean;
  nested?: boolean;
  onNavigate: () => void;
}> = ({ item, isCollapsed, nested, onNavigate }) => (
  <NavLink
    to={item.to || '/'}
    end={item.to === '/'}
    className={({ isActive }) =>
      `sidebar-nav-item${nested ? ' sidebar-nav-child' : ''}${isActive ? ' sidebar-nav-item-active' : ''}`
    }
    title={isCollapsed ? item.label : undefined}
    onClick={onNavigate}
  >
    {!nested && (
      <span className="sidebar-nav-icon">
        <SidebarIcon type={item.iconType} />
        {isCollapsed && !!item.badge && <span className="sidebar-badge-dot" />}
      </span>
    )}
    {(!isCollapsed || nested) && <span className="sidebar-nav-label">{item.label}</span>}
    {(!isCollapsed || nested) && !!item.badge && <span className="sidebar-badge">{item.badge}</span>}
  </NavLink>
);

/**
 * An expandable section (Stock, Scan Inventory, Warehouses). Expanded
 * sidebar: opens inline. Collapsed to icons: opens a fly-out to the right.
 */
const NavGroup: React.FC<{
  item: NavItem;
  isCollapsed: boolean;
  open: boolean;
  onToggle: () => void;
  onNavigate: () => void;
}> = ({ item, isCollapsed, open, onToggle, onNavigate }) => {
  const { pathname } = useLocation();
  const active = groupIsActive(pathname, item);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const flyoutRef = useRef<HTMLDivElement>(null);
  const [flyout, setFlyout] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (!flyout) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (flyoutRef.current?.contains(target) || buttonRef.current?.contains(target)) return;
      setFlyout(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFlyout(null);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [flyout]);

  useEffect(() => setFlyout(null), [pathname]);

  const onClick = () => {
    if (isCollapsed) {
      const rect = buttonRef.current?.getBoundingClientRect();
      setFlyout((f) => (f || !rect ? null : { top: rect.top, left: rect.right + 6 }));
    } else {
      onToggle();
    }
  };

  return (
    <div className={`sidebar-group${open && !isCollapsed ? ' is-open' : ''}`}>
      <button
        ref={buttonRef}
        type="button"
        className={`sidebar-nav-item sidebar-group-head${active ? ' sidebar-group-active' : ''}`}
        onClick={onClick}
        aria-expanded={isCollapsed ? !!flyout : open}
        title={isCollapsed ? item.label : undefined}
      >
        <span className="sidebar-nav-icon"><SidebarIcon type={item.iconType} /></span>
        {!isCollapsed && (
          <>
            <span className="sidebar-nav-label">{item.label}</span>
            <span className="sidebar-group-chevron"><SidebarIcon type="chevron-down" /></span>
          </>
        )}
      </button>
      {!isCollapsed && open && (
        <div className="sidebar-group-children">
          {(item.children || []).map((child) => (
            <NavLinkItem key={child.to} item={child} isCollapsed={false} nested onNavigate={onNavigate} />
          ))}
        </div>
      )}
      {isCollapsed && flyout &&
        createPortal(
          <div ref={flyoutRef} className="sidebar-flyout" style={{ top: flyout.top, left: flyout.left }} role="menu">
            <div className="sidebar-flyout-title">{item.label}</div>
            {(item.children || []).map((child) => (
              <NavLinkItem
                key={child.to}
                item={child}
                isCollapsed={false}
                nested
                onNavigate={() => {
                  setFlyout(null);
                  onNavigate();
                }}
              />
            ))}
          </div>,
          document.body
        )}
    </div>
  );
};

const Sidebar: React.FC<SidebarProps> = ({
  navItems,
  onLogout,
  onNewTask,
  isCollapsed,
  onCollapsedChange,
  width,
  onWidthChange,
  mobileOpen,
  onMobileOpenChange,
}) => {
  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const dragStartWidth = useRef(0);

  const handleResizeStart = useCallback(
    (e: React.MouseEvent) => {
      isDragging.current = true;
      dragStartX.current = e.clientX;
      dragStartWidth.current = width;
      e.preventDefault();
    },
    [width],
  );

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!isDragging.current) return;
      const delta = e.clientX - dragStartX.current;
      const next = Math.min(420, Math.max(160, dragStartWidth.current + delta));
      onWidthChange(next);
    };
    const onMouseUp = () => {
      isDragging.current = false;
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [onWidthChange]);

  const currentWidth = isCollapsed ? 64 : width;
  const { pathname } = useLocation();

  // Groups remember being opened; the group holding the current page is
  // always open so you can see where you are.
  const [openGroups, setOpenGroups] = useState<string[]>(readOpenGroups);
  const toggleGroup = (label: string) =>
    setOpenGroups((prev) => {
      const next = prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label];
      try {
        localStorage.setItem(OPEN_GROUPS_KEY, JSON.stringify(next));
      } catch {
        /* per-viewer convenience only */
      }
      return next;
    });
  const closeMobile = () => onMobileOpenChange(false);

  return (
    <>
      {/* Mobile hamburger */}
      <button
        className="sidebar-hamburger"
        onClick={() => onMobileOpenChange(!mobileOpen)}
        aria-label="Toggle menu"
      >
        <SidebarIcon type={mobileOpen ? 'close' : 'hamburger'} />
      </button>

      {/* Mobile backdrop */}
      <div
        className={`sidebar-backdrop${mobileOpen ? ' sidebar-backdrop-visible' : ''}`}
        onClick={() => onMobileOpenChange(false)}
      />

      {/* Sidebar */}
      <aside
        className={`sidebar${isCollapsed ? ' sidebar-collapsed' : ''}${mobileOpen ? ' sidebar-mobile-open' : ''}`}
        style={{ width: currentWidth }}
      >
        {/* Logo */}
        <div className="sidebar-logo-section">
          <img
            src="/logo.png"
            alt="Logo"
            className="sidebar-logo-img"
          />
        </div>

        {onNewTask && (
          <div className="sidebar-create">
            <button
              type="button"
              className={`sidebar-create-btn${isCollapsed ? ' is-compact' : ''}`}
              onClick={() => {
                onNewTask();
                closeMobile();
              }}
              title={isCollapsed ? 'New task' : undefined}
              aria-label="New task"
            >
              <SidebarIcon type="plus" />
              {!isCollapsed && <span>New task</span>}
            </button>
          </div>
        )}

        {/* Nav */}
        <nav className="sidebar-nav">
          {navItems.map((item) =>
            item.children ? (
              <NavGroup
                key={item.label}
                item={item}
                isCollapsed={isCollapsed}
                open={openGroups.includes(item.label) || groupIsActive(pathname, item)}
                onToggle={() => toggleGroup(item.label)}
                onNavigate={closeMobile}
              />
            ) : (
              <NavLinkItem key={item.to} item={item} isCollapsed={isCollapsed} onNavigate={closeMobile} />
            )
          )}
        </nav>

        {/* Footer */}
        <div className="sidebar-footer">
          <button
            className="sidebar-nav-item sidebar-logout-btn"
            onClick={() => { onLogout(); onMobileOpenChange(false); }}
            title={isCollapsed ? 'Logout' : undefined}
          >
            <span className="sidebar-nav-icon">
              <SidebarIcon type="logout" />
            </span>
            {!isCollapsed && <span className="sidebar-nav-label">Logout</span>}
          </button>

          <button
            className="sidebar-collapse-btn"
            onClick={() => onCollapsedChange(!isCollapsed)}
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <span className="sidebar-nav-icon">
              <SidebarIcon type={isCollapsed ? 'chevron-right' : 'chevron-left'} />
            </span>
            {!isCollapsed && <span className="sidebar-nav-label">Collapse</span>}
          </button>
        </div>

        {/* Resize handle — desktop only, not when collapsed */}
        {!isCollapsed && (
          <div
            className="sidebar-resize-handle"
            onMouseDown={handleResizeStart}
          />
        )}
      </aside>
    </>
  );
};

export default Sidebar;
